import {
  createElement, ReactElement, useCallback, useEffect, useRef, useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { parseApiError } from 'shared/api/apiError'
import { transitOperationCacheRoots } from 'features/declaration/transit-operation/keys'
import useSigningSession from 'features/transit-operation-audit/components/useSigningSession'
import { SigningSession } from 'features/transit-operation-audit/api/signingSession'
import {
  cancelSerbianFetchRunV3,
  getSerbianFetchRunV3,
  startSerbianFetchRunV3,
  submitSerbianFetchSignaturesV3,
} from '../api/http'
import {
  RunCounts,
  RunStatusResponse,
  SerbianFetchErrorCode,
  SigningItem,
  StartRunResponse,
} from '../models/types'

export type SerbianInboundFetchStep = 'IDLE' | 'STARTING' | 'SIGNING' | 'PROCESSING'

export interface SerbianInboundFetchProgress {
  done: number
  total: number
}

type Translate = ReturnType<typeof useTranslation>['t']

export const RUN_ID_STORAGE_KEY = 'serbiaInboundFetch.runId'
export const STATUS_POLL_INTERVAL_MS = 2000
export const SIGNING_TIME_LIMIT_MS = 5 * 60 * 1000
export const MAX_CONSECUTIVE_STATUS_FAILURES = 30

const COUNT_KEYS: (keyof RunCounts)[] = [
  'applied',
  'alreadyApplied',
  'noMatch',
  'unreadable',
  'dropped',
  'deliverRefused',
  'confirmRefused',
  'retry',
]

function readStoredRunId(): string | null {
  try {
    return sessionStorage.getItem(RUN_ID_STORAGE_KEY)
  } catch {
    return null
  }
}

function storeRunId(runId: string) {
  try {
    sessionStorage.setItem(RUN_ID_STORAGE_KEY, runId)
  } catch {
    // Without storage a reload loses the toast; the run itself is unaffected.
  }
}

function clearStoredRunId() {
  try {
    sessionStorage.removeItem(RUN_ID_STORAGE_KEY)
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

async function cancelQuietly(runId: string) {
  try {
    await cancelSerbianFetchRunV3(runId)
  } catch {
    // The run expires on its own; a failed cancel changes nothing for the administrator.
  }
}

function hasCode(message: string | null | undefined, code: string): boolean {
  return !!message && message.startsWith(code)
}

/**
 * Translates a code from the error body or a run's `failureMessage`. Codes without a text of their
 * own are shown inside the generic failure text.
 */
function describeCode(t: Translate, code: string): string {
  if (hasCode(code, SerbianFetchErrorCode.POLL_FAILED)) {
    const separator = code.indexOf(': ')
    return t('serbiaInboundFetch.pollFailed', { reason: separator >= 0 ? code.substring(separator + 2) : '' })
  }
  if (hasCode(code, SerbianFetchErrorCode.RUN_OPEN)) {
    return t('serbiaInboundFetch.runOpen')
  }
  if (hasCode(code, SerbianFetchErrorCode.RUN_EXPIRED)) {
    return t('serbiaInboundFetch.expired')
  }
  if (hasCode(code, SerbianFetchErrorCode.RUN_OWNED_BY_ANOTHER_ADMINISTRATOR)) {
    return t('serbiaInboundFetch.ownedByAnother')
  }
  if (hasCode(code, SerbianFetchErrorCode.SIGNING_NOT_COMPLETED)) {
    return t('serbiaInboundFetch.signingNotCompleted')
  }
  return t('serbiaInboundFetch.failed', { code })
}

/**
 * The run's own codes are matched first: the server refuses a foreign administrator's signatures with
 * 403 and a code, while a 403 without one is the role check.
 */
function describeApiError(t: Translate, error: unknown): string {
  const { status, reason, message } = parseApiError(error)
  if (message && Object.values(SerbianFetchErrorCode).some((code) => hasCode(message, code))) {
    return describeCode(t, message)
  }
  if (status === 403 || reason === 'forbidden') {
    return t('serbiaInboundFetch.forbidden')
  }
  return t('serbiaInboundFetch.failed', { code: message ?? reason })
}

function describeFailedRun(t: Translate, status: RunStatusResponse): string {
  if (status.failureMessage) {
    return describeCode(t, status.failureMessage)
  }
  return t('serbiaInboundFetch.failed', { code: status.phase })
}

function resultNode(t: Translate, status: RunStatusResponse): ReactElement {
  const lines = COUNT_KEYS.map((key) => createElement(
    'div',
    { key },
    t(`serbiaInboundFetch.${key}`, { count: status.counts?.[key] ?? 0 }),
  ))
  if (status.waitingCount > 0) {
    lines.push(createElement(
      'div',
      { key: 'stillWaiting' },
      t('serbiaInboundFetch.stillWaiting', { count: status.waitingCount }),
    ))
  }
  return createElement(
    'div',
    null,
    createElement('strong', null, t('serbiaInboundFetch.resultTitle')),
    ...lines,
  )
}

/**
 * The administrator's manual fetch of Serbian customs replies: one run is polled from the gateway,
 * every envelope is signed on one card session, the signatures go back in one call and the result is
 * awaited by polling the run's status. The run id is kept in sessionStorage from the moment the server
 * accepts the signatures, or may have accepted them when the call failed without an answer, so a reload
 * or a return to the table during the processing still ends in the result toast; a reload during the
 * signing pass leaves the run to the server's five-minute expiry. A run still signing when polled never
 * got the signatures and is cancelled, unless they land first.
 */
function useSerbianInboundFetch() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { getSession, clearSession } = useSigningSession()
  const [step, setStep] = useState<SerbianInboundFetchStep>('IDLE')
  const [progress, setProgress] = useState<SerbianInboundFetchProgress | null>(null)
  const mountedRef = useRef(true)
  const busyRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const updateStep = useCallback((next: SerbianInboundFetchStep) => {
    if (mountedRef.current) setStep(next)
  }, [])

  const updateProgress = useCallback((next: SerbianInboundFetchProgress | null) => {
    if (mountedRef.current) setProgress(next)
  }, [])

  const finish = useCallback(() => {
    busyRef.current = false
    updateStep('IDLE')
    updateProgress(null)
  }, [updateProgress, updateStep])

  /**
   * Polls the run until it is terminal. Stops quietly on unmount or when the login has expired and
   * leaves the stored run id in place, so the next mount resumes and shows the toast exactly once.
   */
  const awaitResult = useCallback(async (runId: string) => {
    let consecutiveFailures = 0
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(STATUS_POLL_INTERVAL_MS)
      if (!mountedRef.current) return

      let status: RunStatusResponse
      try {
        // eslint-disable-next-line no-await-in-loop
        status = await getSerbianFetchRunV3(runId)
      } catch (error) {
        const { status: httpStatus, reason } = parseApiError(error)
        if (reason === 'unauthorized') return
        if (reason === 'networkError' || reason === 'serverError') {
          consecutiveFailures += 1
          if (consecutiveFailures < MAX_CONSECUTIVE_STATUS_FAILURES) {
            // eslint-disable-next-line no-continue
            continue
          }
          toast.error(t('serbiaInboundFetch.failed', { code: reason }))
          break
        }
        if (httpStatus === 404) {
          toast.error(t('serbiaInboundFetch.resultUnknown'))
        } else {
          toast.error(describeApiError(t, error))
        }
        break
      }
      if (!mountedRef.current) return
      consecutiveFailures = 0

      if (status.phase === 'DONE') {
        toast.success(resultNode(t, status), { autoClose: false, closeOnClick: false, draggable: false })
        transitOperationCacheRoots.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))
        break
      }
      if (status.phase === 'EXPIRED') {
        toast.error(t('serbiaInboundFetch.signaturesTooLate'))
        break
      }
      if (status.phase === 'FAILED') {
        toast.error(describeFailedRun(t, status))
        break
      }
      if (status.phase === 'SIGNING') {
        // eslint-disable-next-line no-await-in-loop
        await cancelQuietly(runId)
      }
    }
    clearStoredRunId()
  }, [queryClient, t])

  /**
   * Signs every item in the order received on one session. Returns null when the five minutes from the
   * click ran out before the last item; throws when the card or the provider fails.
   */
  const signAll = useCallback(async (
    session: SigningSession,
    items: SigningItem[],
    clickedAt: number,
  ): Promise<SigningItem[] | null> => {
    const signatures: SigningItem[] = []
    for (let i = 0; i < items.length; i += 1) {
      if (Date.now() > clickedAt + SIGNING_TIME_LIMIT_MS) return null
      updateProgress({ done: i, total: items.length })
      const { index, operation, data } = items[i]
      // eslint-disable-next-line no-await-in-loop
      const { signature } = await session.sign(data)
      signatures.push({ index, operation, data: signature })
    }
    updateProgress({ done: items.length, total: items.length })
    return signatures
  }, [updateProgress])

  const start = useCallback(async () => {
    if (busyRef.current) return
    busyRef.current = true
    const clickedAt = Date.now()

    try {
      updateStep('STARTING')
      let started: StartRunResponse
      try {
        started = await startSerbianFetchRunV3()
      } catch (error) {
        if (hasCode(parseApiError(error).message, SerbianFetchErrorCode.RUN_OPEN)) {
          toast.warn(t('serbiaInboundFetch.runOpen'))
        } else {
          toast.error(describeApiError(t, error))
        }
        return
      }

      const { runId, items } = started
      if (items.length === 0) {
        toast.info(t('serbiaInboundFetch.noMessages'))
        return
      }

      updateStep('SIGNING')
      let session: SigningSession
      let signatures: SigningItem[] | null
      try {
        session = await getSession('RS')
        signatures = await signAll(session, items, clickedAt)
      } catch (error) {
        const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown }
        // eslint-disable-next-line no-console
        console.error('[serbia-inbound-fetch] signing failed', { code, message })
        await cancelQuietly(runId)
        clearSession()
        toast.error(t('serbiaInboundFetch.signingNotCompleted'))
        return
      }
      if (!signatures) {
        await cancelQuietly(runId)
        toast.error(t('serbiaInboundFetch.expired'))
        return
      }

      try {
        await submitSerbianFetchSignaturesV3(runId, { certificateDer: session.certificate, signatures })
      } catch (error) {
        const { reason } = parseApiError(error)
        if (reason !== 'networkError' && reason !== 'serverError') {
          toast.error(describeApiError(t, error))
          return
        }
      }

      storeRunId(runId)
      updateProgress(null)
      updateStep('PROCESSING')
      await awaitResult(runId)
    } finally {
      finish()
    }
  }, [awaitResult, clearSession, finish, getSession, signAll, t, updateProgress, updateStep])

  useEffect(() => {
    if (busyRef.current) return
    const runId = readStoredRunId()
    if (!runId) return

    busyRef.current = true
    updateStep('PROCESSING')
    awaitResult(runId).finally(finish)
  }, [awaitResult, finish, updateStep])

  return {
    step,
    progress,
    start,
  }
}

export default useSerbianInboundFetch
